import { ChevronRight } from '@mui/icons-material';
import { getIn } from 'formik';
import React, {
  cloneElement,
  Dispatch,
  ReactElement,
  SetStateAction,
  useRef,
  useState,
} from 'react';
import { PopoverToModal } from '~/Components';
import { CHARCOAL_GREY, GREY } from '~/Constants/Colors.constants';
import { useBoolean, useLanguage } from '~/Hooks';
import { useItemCreationContext } from '~/ModularComponents/ItemCreation/ItemCreation';
import { Button } from '~/UIKit';
import { makeStyles } from '~/UIKit/Style';

interface IInputSelectorProps {
  name: string;
  buttonText: string;
  children: ReactElement<{
    name: string;
    isOpen: boolean;
    setOnSaveCallback: Dispatch<SetStateAction<() => void>>;
  }>;
}

const useStyles = (trainerSelected: boolean) =>
  makeStyles(
    () => ({
      wrapper: {
        width: '100%',
        display: 'flex',
        overflowWrap: 'anywhere',
      },
      button: {
        color: trainerSelected ? CHARCOAL_GREY : GREY,
        fontWeight: 'normal',
        marginLeft: 'auto',
        paddingRight: 0,
        // Offsets Chevron padding
        marginRight: '-8px',
      },
      ChevronRight: {
        color: GREY,
        display: 'inherit',
        alignItems: 'inherit',
        justifyContent: 'inherit',
      },
    }),
    { name: 'inputSelector' },
  );

export const InputSelector = ({ name, children, buttonText }: IInputSelectorProps) => {
  // State
  const [onSaveCallback, setOnSaveCallback] = useState(() => () => {});
  // hooks
  const { setFalse: close, setTrue: open, state: isOpen } = useBoolean(false);
  const { isLoading, disabled, values } = useItemCreationContext();

  const selectedElements = getIn(values, name) as string[];
  const isDisabled = getIn(disabled, name) as boolean;
  const arrayLength = selectedElements?.length || 0;

  const handleActionTaken = () => {
    onSaveCallback();
    close();
  };

  const { classes } = useStyles(!!arrayLength)();
  const { t } = useLanguage();

  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <div className={classes.wrapper} data-testid="selectioninput-wrapper">
      <Button
        type="button"
        disabled={isDisabled || isLoading}
        className={classes.button}
        onClick={open}
        data-testid="selectioninput-button"
        ref={buttonRef}
      >
        {arrayLength ? arrayLength : buttonText}
        <span className={classes.ChevronRight}>
          <ChevronRight />
        </span>
      </Button>
      <PopoverToModal
        open={isOpen}
        onClose={close}
        anchorEl={buttonRef.current}
        title={buttonText}
        actionName={t('app.checkin.select.done')}
        onActionTaken={handleActionTaken}
      >
        {cloneElement(children, {
          name,
          isOpen,
          setOnSaveCallback,
        })}
      </PopoverToModal>
    </div>
  );
};
